import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLexicon } from '../../../hooks/useLexicon'
import { EventCard, EventCardWrapper, EventContent } from '../../../pages/tournametns/team/EventsMatches';
import { Dropdown, DropdownButton, DropdownContent, DropdownItem } from '../../../pages/tournametns/player/EventsPlayer';
import { DropdownWrapper, OpenedDropdown } from '../../table/MatchTable';
import TextInput from '../../inputs/TextInput';
import useOutsideClick from '../../../hooks/useOutsideClick';

import arrowIconUp from "../../../images/up.svg";
import arrowIconDown from "../../../images/down.svg";
import { getUniqueArrayObjectsByKey } from '../../../helpers';
import { formatMatchScore as formatMatchScoreValue } from '../../../helpers/matchScore';
import { ChoosenSelectionsPopover } from './ChoosenSelectionsPopover';
import styled from 'styled-components';
import DateRangeSlider from './DateRangeSlider';

export const PopoverContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 350px;
    max-height: 350px;
    overflow: auto;
    scrollbar-width: auto;
`

export const PopoverContentItem = styled.div`
    b {
        font-family: "SatoshiRegular";
        font-size: 14px;
    }

    div {
        font-family: "SatoshiRegular";
        font-size: 13px;
        color: ${(props) => props.theme.white};
    }

    .matches-list {
        white-space: pre;
        max-height: 150px;
        overflow-x: auto;
        scrollbar-width: auto;
    }

`

export const ChooseSeasonSelector = React.memo((props) => {
    const {
        availableSeasons = [],
        getData,
        handleChangeMatches,
        resetTrigger,
        onChangeFilters,
        hideTopFilters = false,
    } = props;

    const l = useLexicon();

    const [openSeasonSelect, setOpenSeasonSelect] = useState(false);
    const [openCompetitionSelect, setOpenCompetitionSelect] = useState(false);
    const [openMatchesCountSelect, setOpenMatchesCountSelect] = useState(false);
    const [openMatchesSelect, setOpenMatchesSelect] = useState(false);

    const [loading, setLoading] = useState(false);
    const [cache, setCache] = useState({});

    const [competitions, setCompetitions] = useState([]);
    const [matches, setMatches] = useState([]);

    // season
    const [selectedSeason, setSelectedSeason] = useState(() => availableSeasons?.length > 0 ? [availableSeasons?.[0]] : []);
    const selectedSeasonIds = useMemo(() => {
        if (selectedSeason?.length > 0) {
            return selectedSeason.flatMap(item => item.id);
        }
        return [];
    }, [selectedSeason]);
    const selectedSeasonSelectString = useMemo(() => {
        if (selectedSeason.length > 0) {
            const years = selectedSeason.flatMap(item => item.years);
            if (years.length > 2) {
                return `${years[0]}, ${years[1]} + ${years.length - 2}`
            } else {
                return years.join(', ')
            }
        }
        return l(992)
    }, [selectedSeason, l]);

    // competition
    const [selectedCompetition, setSelectedCompetition] = useState([]);
    const selectedCompetitionIds = useMemo(() => {
        if (selectedCompetition?.length > 0) {
            return selectedCompetition.flatMap(item => +item.competition_id);
        }
        return [];
    }, [selectedCompetition]);
    const selectedCompetitonSelectString = useMemo(() => {
        if (selectedCompetition.length > 0) {
            const competitions = selectedCompetition.flatMap(item => item.competition);
            if (competitions.length > 1) {
                return `${competitions[0]} + ${competitions.length - 1}`
            } else {
                return competitions.join(', ')
            }
        }
        return l(993)
    }, [selectedCompetition, l]);

    // pre-set match count
    const matchesCountList = useMemo(
        () => [
            {
                id: 1,
                value: matches?.length || 0,
                title: l(1066),
            },
            {
                id: 2,
                value: 3,
                title: l(1067),
            },
            {
                id: 3,
                value: 5,
                title: l(1068),
            },
            {
                id: 4,
                value: 10,
                title: l(1069),
            },
        ],
        [matches, l]
    );
    const [selectedMatchCount, setSelectedMatchCount] = useState(null);
    const selectedMatchCountRef = useRef(null);

    // matches
    const [selectedMathes, setSelectedMatches] = useState([]);
    const selectedMatchesIds = useMemo(() => {
        if (selectedMathes?.length > 0) {
            return selectedMathes.flatMap(item => item.id);
        }
        return [];
    }, [selectedMathes]);

    const formatMatchDate = useCallback((matchDate) => {
        if (!matchDate) {
            return '-';
        }

        const parsedDate = new Date(matchDate);
        if (!Number.isFinite(parsedDate.getTime())) {
            return '-';
        }

        const day = String(parsedDate.getDate()).padStart(2, '0');
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const year = parsedDate.getFullYear();

        return `${day}/${month}/${year}`;
    }, []);

    const formatMatchScore = useCallback((match) => {
        return formatMatchScoreValue(match, { separator: ' : ' });
    }, []);

    const getMatchDisplayLabel = useCallback((match) => {
        const homeTeam = match?.home_team || '-';
        const awayTeam = match?.away_team || '-';
        const score = formatMatchScore(match);
        const competitionOrSeason =
            match?.competition ||
            match?.season ||
            match?.years ||
            selectedSeasonSelectString ||
            '-';
        const formattedDate = formatMatchDate(match?.date);

        return `${homeTeam} ${score} ${awayTeam} | ${competitionOrSeason} | ${formattedDate}`;
    }, [formatMatchDate, formatMatchScore, selectedSeasonSelectString]);

    const selectedMatchesSelectString = useMemo(() => {
        if (selectedMathes.length > 0) {
            const matches = selectedMathes.map((item) => getMatchDisplayLabel(item));
            if (selectedMathes.length > 1) {
                return `${matches[0]} + ${matches.length - 1}`
            } else {
                return matches.join(', ')
            }
        }
        return l(12)
    }, [getMatchDisplayLabel, selectedMathes, l]);


    // helpers 
    const getCachedDataOrFetch = async (season) => {
        try {
            if (cache[season]) {
                return cache[season];
            }
            const data = await getData?.(season);
            setCache(prev => ({ ...prev, [season]: data }))
            return data;
        } catch (e) {
            console.error('get cached data err', e.message);
            return null;
        }
    }

    const resetStateSelector = () => {
        setCompetitions([]);
        setSelectedCompetition([]);
        setSelectedMatchCount(null);
        selectedMatchCountRef.current = null;
        setMatches([]);
        setSelectedMatches([]);
        setSelectedCompetition([])
        setCompetitions([])
    }

    useEffect(() => {
        if (resetTrigger > 0) {
            resetStateSelector()
            setSelectedSeason([])
        }
    }, [resetTrigger])

    useEffect(() => {
        if (selectedSeasonIds.length > 0) {
            const queryArr = selectedSeasonIds.map(id => getCachedDataOrFetch(id));
            setLoading(true)
            Promise.allSettled(queryArr)
                .then(res => {
                    const matchesList = res.flatMap(item => item.value?.matches_list || []);
                    const uniqueCompetitionList = getUniqueArrayObjectsByKey(
                        matchesList,
                        "competition_id"
                    );
                    setCompetitions(uniqueCompetitionList)
                    setSelectedCompetition(uniqueCompetitionList)
                    setMatches(matchesList)
                    if (selectedMatchCount) {
                        if (selectedMatchCount.id === 1) {
                            setSelectedMatchCount(
                                {
                                    id: 1,
                                    value: matchesList?.length || 0,
                                    title: l(1066),
                                }
                            )
                        } else {
                            setSelectedMatchCount(selectedMatchCount)
                        }
                    } else {
                        setSelectedMatchCount(
                            {
                                id: 1,
                                value: matchesList?.length || 0,
                                title: l(1066),
                            }
                        )
                    }
                })
                .finally(() => setLoading(false))
        } else {
            resetStateSelector()
        }
    }, [selectedSeasonIds])

    useEffect(() => {
        handleChangeMatches?.(selectedMatchesIds)
    }, [selectedMatchesIds])

    useEffect(() => {
        if (selectedMatchCount) {
            const slicedMatches = matches?.filter((match) => selectedCompetitionIds.includes(+match.competition_id))?.slice(0, selectedMatchCount.value)
            setSelectedMatches(slicedMatches)
        }
    }, [selectedMatchCount, matches, selectedCompetitionIds])

    useEffect(() => {
        onChangeFilters?.(selectedSeason, selectedCompetition, selectedMathes)
    }, [selectedCompetition, selectedMathes, selectedSeason])

    // outside clicker handlers
    const seasonDropdownRef = useOutsideClick(() => {
        if (openSeasonSelect) {
            setOpenSeasonSelect(false);
        }
    });

    const competitionDropdownRef = useOutsideClick(() => {
        if (openCompetitionSelect) {
            setOpenCompetitionSelect(false);
        }
    });

    const mathesCountDropdownRef = useOutsideClick(() => {
        if (openMatchesCountSelect) {
            setOpenMatchesCountSelect(false);
        }
    });

    const matchesListDropdownRef = useOutsideClick(() => {
        if (openMatchesSelect) {
            setOpenMatchesSelect(false);
        }
    });
    // end outside clicker handlers

    // season selector handlers
    const handleSeasonChoose = (season) => {
        setSelectedSeason(prev => {
            const isSelected = prev.some((selected) => selected.id === season.id);

            if (isSelected) {
                return prev.filter((selected) => selected.id !== season.id);
            } else {
                return [...prev, season];
            }
        })
    };
    // end season selector handlers

    // competition selector handlers
    const handleCompetitionChoose = (competition) => {
        setSelectedCompetition(prev => {
            const isSelected = prev.some((selected) => +selected.competition_id === +competition.competition_id);

            if (isSelected) {
                return prev.filter((selected) => +selected.competition_id !== +competition.competition_id);
            } else {
                return [...prev, competition];
            }
        })
    };
    // end season selector handlers

    // pre-set match count selector handlers
    const handleMatchCountChoose = (value) => {
        setSelectedMatchCount(value)
        selectedMatchCountRef.current = value
        setOpenMatchesCountSelect(false)
    };
    // end pre-set match count selector handlers

    // match selector handlers
    const handleMatchChoose = (match) => {
        setSelectedMatches(prev => {
            const isSelected = prev.some((selected) => selected.id === match.id);

            if (isSelected) {
                return prev.filter((selected) => selected.id !== match.id);
            } else {
                return [...prev, match];
            }
        })
    };
    // end match selector handlers

    const handleDateRangeChange = useCallback((startDate, endDate) => {
        const toUtcDayKeyTimestamp = (value) => {
            const timestamp = new Date(value).getTime();
            if (!Number.isFinite(timestamp)) {
                return null;
            }

            const date = new Date(timestamp);
            return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
        };

        const startDayTimestamp = toUtcDayKeyTimestamp(startDate);
        const endDayTimestamp = toUtcDayKeyTimestamp(endDate);
        if (startDayTimestamp === null || endDayTimestamp === null) {
            return;
        }

        const matchesInRange = (matches || []).filter((match) => {
            if (!match?.date) {
                return false;
            }

            const matchDayTimestamp = toUtcDayKeyTimestamp(match.date);
            if (matchDayTimestamp === null) {
                return false;
            }

            return matchDayTimestamp >= startDayTimestamp && matchDayTimestamp <= endDayTimestamp;
        });

        setSelectedMatches(matchesInRange);
    }, [matches]);

    const PopoverContent = useMemo(() => {
        return (
            <PopoverContentContainer>
                {
                    selectedSeason.length > 0 && (
                        <PopoverContentItem>
                            <b>{l(453)}</b>
                            <div>
                                {selectedSeason?.flatMap(item => item.years)?.join(', ')}
                            </div>
                        </PopoverContentItem>
                    )
                }
                {
                    selectedCompetition.length > 0 && (
                        <PopoverContentItem>
                            <b>{l(454)}</b>
                            <div>
                                {selectedCompetition?.flatMap(item => item.competition)?.join(', ')}
                            </div>
                        </PopoverContentItem>
                    )
                }
                {
                    selectedMatchCount?.title && (
                        <PopoverContentItem>
                            <b>{l(455)}</b>
                            <div>
                                {selectedMatchCount?.title}
                            </div>
                        </PopoverContentItem>
                    )
                }

                {
                    selectedMathes.length > 0 && (
                        <PopoverContentItem>
                            <b>{l(7)}</b>
                            <div className='matches-list'>
                                {selectedMathes?.map((item) => `- ${getMatchDisplayLabel(item)}`)?.join('\n')}
                            </div>
                        </PopoverContentItem>
                    )
                }
            </PopoverContentContainer>
        )
    }, [selectedSeason, selectedCompetition, selectedMatchCount, selectedMathes, l, getMatchDisplayLabel])

    return (
        <EventCardWrapper>
            <h3>
                {l(995)}
                {selectedSeason?.length > 0 && (
                    <ChoosenSelectionsPopover content={PopoverContent}>Show selections</ChoosenSelectionsPopover>
                )}
            </h3>
            <EventCard>
                <EventContent>
                    {!hideTopFilters && (
                        <>
                            {/* years selector */}
                            <Dropdown ref={seasonDropdownRef} disabled={loading}>
                                <DropdownButton
                                    onClick={() => setOpenSeasonSelect(prev => !prev)}
                                >
                                    {selectedSeasonSelectString}
                                    <img
                                        src={openSeasonSelect ? arrowIconUp : arrowIconDown}
                                        alt='arrow'
                                    />
                                </DropdownButton>
                                <DropdownContent $show={openSeasonSelect}>
                                    {availableSeasons?.length > 0 ?
                                        availableSeasons?.map((item) => (
                                            <DropdownItem
                                                key={item?.id}
                                                selected={selectedSeasonIds.includes(+item.id)}
                                                onClick={() => handleSeasonChoose(item)}
                                            >
                                                {item?.years}
                                            </DropdownItem>
                                        )) : (
                                            <DropdownItem>
                                                {l(464)}
                                            </DropdownItem>
                                        )}
                                </DropdownContent>
                            </Dropdown>

                            {/* competition selector */}
                            <Dropdown ref={competitionDropdownRef} disabled={loading || selectedSeasonIds?.length === 0}>
                                <DropdownButton
                                    onClick={() => setOpenCompetitionSelect(prev => !prev)}
                                >
                                    {selectedCompetitonSelectString}
                                    <img src={openCompetitionSelect ? arrowIconUp : arrowIconDown} alt='arrow' />
                                </DropdownButton>
                                <DropdownContent $show={openCompetitionSelect}>
                                    {competitions.length > 0 ? competitions?.map((item) => (
                                        <DropdownItem
                                            key={item?.competition_id}
                                            selected={selectedCompetitionIds.includes(+item.competition_id)}
                                            onClick={() => handleCompetitionChoose(item)}
                                        >
                                            {item?.competition}
                                        </DropdownItem>
                                    )) : (
                                        <DropdownItem>
                                            {l(464)}
                                        </DropdownItem>
                                    )}
                                </DropdownContent>
                            </Dropdown>

                            {/* last matches pre-set selector */}
                            <DropdownWrapper
                                ref={mathesCountDropdownRef}
                                disabled={loading || matches?.length === 0 || selectedCompetitionIds?.length === 0 || selectedSeasonIds.length === 0}
                            >
                                <div
                                    style={{ position: "relative" }}
                                    onClick={() => setOpenMatchesCountSelect(prev => !prev)}
                                >
                                    <TextInput
                                        type="text"
                                        value={!selectedMatchCount ? l(994) : selectedMatchCount?.title}
                                        readOnly
                                    />
                                    <img
                                        src={openMatchesCountSelect ? arrowIconUp : arrowIconDown}
                                        alt={arrowIconDown}
                                        style={{
                                            color: "#fff",
                                            position: "absolute",
                                            right: 0,
                                            cursor: "pointer",
                                        }}
                                    />
                                </div>
                                {openMatchesCountSelect && (
                                    <OpenedDropdown>
                                        {matchesCountList?.map((item) => (
                                            <div key={item.id} onClick={() => handleMatchCountChoose(item)}>
                                                {item.title}
                                            </div>
                                        ))}
                                    </OpenedDropdown>
                                )}
                            </DropdownWrapper>
                        </>
                    )}

                    <DateRangeSlider
                        matches={matches}
                        selectedMatchIds={selectedMatchesIds}
                        onRangeChange={handleDateRangeChange}
                    />

                    {/* matches selector */}
                    <Dropdown ref={matchesListDropdownRef} disabled={loading || selectedSeasonIds.length === 0 || selectedCompetitionIds.length === 0}>
                        <DropdownButton
                            onClick={() => setOpenMatchesSelect(prev => !prev)}
                        >
                            {selectedMatchesSelectString}
                            <img src={openMatchesSelect ? arrowIconUp : arrowIconDown} alt='arrow' />
                        </DropdownButton>
                        <DropdownContent $show={openMatchesSelect}>
                            {matches?.length > 0 ? matches?.map((item, index) => (
                                <DropdownItem
                                    key={index}
                                    selected={selectedMatchesIds.includes(item.id)}
                                    onClick={() => handleMatchChoose(item)}
                                >
                                    {getMatchDisplayLabel(item)}
                                </DropdownItem>
                            )) : (
                                <DropdownItem
                                >
                                    {l(464)}
                                </DropdownItem>
                            )}
                        </DropdownContent>
                    </Dropdown>
                </EventContent>
            </EventCard>
        </EventCardWrapper>
    )
})
